import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LazyMotion, m, useInView, AnimatePresence } from 'framer-motion';
import { loadMotionFeatures } from '@/lib/motionFeatures';
import { api, GATEWAY_BASE } from '@/lib/api';
import { egp } from '@/lib/format';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CodeBlock, ProviderBadge, ProviderFilter, sortByProvider, contactIconFor, DiscountBadge, PriceTag } from '@/components/Common';
import { contactHref } from '@/lib/contact';
import { useSeo, SITE } from '@/lib/useSeo';
import {
  Sparkles, KeyRound, Zap, ShieldCheck, Wallet, ArrowLeft, Check,
  Cpu, Image as ImageIcon, Gauge, Terminal, Infinity as InfinityIcon,
  Layers, LineChart, Lock, Rocket, Star, Gift, LifeBuoy, Video,
  ChevronDown, ChevronUp, Tags, Award, Wrench, BadgeCheck, X, MessageCircle, Store,
} from 'lucide-react';

const HSYS_URL = 'https://hsys.io';

/* ---------- motion helpers ---------- */
const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.09 } } };

const Reveal = ({ children, className = '', delay = 0 }) => (
  <m.div
    className={className}
    variants={fadeUp}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.25 }}
    transition={{ delay }}
  >
    {children}
  </m.div>
);

const Counter = ({ to, suffix = '', decimals = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1600;
    const tick = (now) => {
      const t = Math.min((now - start) / dur, 1);
      setVal(to * (1 - Math.pow(1 - t, 3)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);
  return (
    <span ref={ref} className="tabular-nums">
      {val.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}
    </span>
  );
};

const onGlowMove = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
};

const FAQ = [
  { q: 'إزاي أشتري AI API بالجنيه المصري؟', a: 'في 3 خطوات: سجّل على soal.help برقم موبايلك (من غير فيزا ولا بريد إلكتروني)، اشحن رصيدك بأي مبلغ من 10 ج.م بفودافون كاش أو انستاباي أو تحويل بنكي، وأنشئ مفتاح API واحد متوافق مع OpenAI. المفتاح بيفتح لك GPT-5.6 Sol و Claude Sonnet 5 و Gemini 3.1 Pro وتوليد الصور والفيديو — وبتدفع حسب استهلاكك الفعلي بس بدون اشتراك شهري.' },
  { q: 'هل بتوفروا اشتراكات المواقع الرسمية نفسها (ChatGPT Plus / Claude Pro / Gemini)؟', a: 'لا، احنا مش بنبيع اشتراكات المواقع الرسمية. soal.help بتوفّر مفاتيح API متوافقة مع OpenAI تفتح لك GPT و Claude و Gemini وتوليد الصور والفيديو — بالجنيه المصري وبنظام الدفع حسب الاستهلاك بدون اشتراك شهري ولا فيزا.' },
  { q: 'إزاي soal.help شغّالة؟', a: 'تسجّل حساب بموبايلك بالجنيه المصري (بدون فيزا)، تشحن رصيدك بأي مبلغ من 10 ج.م وفوق عن طريق فودافون كاش أو انستاباي أو تحويل بنكي، وتاخد مفتاح API واحد متوافق مع OpenAI. المفتاح ده يفتح لك GPT-5.6 Sol و Claude Sonnet 5 و Gemini 3.1 Pro و Sora 2 و Nano Banana وأي موديل جديد بيتضاف تلقائيًا، والخصم بيتم من رصيدك حسب الاستهلاك الفعلي بالجنيه.' },
  { q: 'إيه الفرق بين soal.help و اشتراك ChatGPT Plus؟', a: 'ChatGPT Plus بيكلّفك 20 دولار شهريًا (يعني 1000+ ج.م) وبيديك موديل واحد، من واجهة واحدة، وبيتطلب فيزا/ماستركارد أجنبية. soal.help pay-as-you-go بالجنيه المصري: تشحن اللي انت عايزه (من 10 ج.م)، تستخدم أي موديل من 25+ موديل، من أي أداة (Cursor · Claude Code · n8n · Codex CLI)، بدون اشتراك ثابت وبدون فيزا. الاستخدام العادي بيطلع 30-80 ج.م/شهر بس.' },
  { q: 'هل في حدود أو ليمت على الاستخدام؟', a: 'لا يوجد أي حدود شهرية أو يومية. استخدم أي موديل (GPT-5.6 · Claude Sonnet 5 · Gemini 3.1)، أي عدد من الطلبات، وقت ما تحب — طول ما رصيدك متاح. أنت المتحكم الوحيد.' },
  { q: 'هل أحتاج أكثر من مفتاح؟', a: 'لا. مفتاح API واحد يفتح لك كل موديلات الذكاء الاصطناعي (GPT · Claude · Gemini) بالإضافة لتوليد الصور (Nano Banana) وتوليد الفيديو (Sora 2). البرنامج بتاعك (Cursor · Chatbox · n8n) هو اللي يحدد الموديل في كل طلب من خلال حقل model في الـ request.' },
  { q: 'هل الواجهة متوافقة مع OpenAI؟', a: 'نعم بالكامل — endpoint واحد /v1/chat/completions و /v1/images/generations و /v1/videos/generations. غيّر الـ base_url في مكتبتك (Python openai أو TypeScript ai-sdk أو أي مكتبة تانية) لـ https://soal.help/api واستخدم مفتاحك، بدون تغيير أي كود آخر.' },
  { q: 'هل يشتغل مع Claude Code من Anthropic؟', a: 'نعم — soal.help كمان بتدعم Anthropic Messages API الرسمي (/v1/messages). ظبّط المتغيّرين ANTHROPIC_BASE_URL=https://soal.help/api و ANTHROPIC_AUTH_TOKEN=sk-... وشغّل claude عادي. Function Calling وقراءة/كتابة الملفات وتشغيل الأوامر — كلها شغّالة زي Claude Code الرسمي بالظبط. تقدر تختار موديل GPT أو Gemini كمان من داخل Claude Code عبر ANTHROPIC_MODEL.' },
  { q: 'هل يشتغل مع Codex CLI من OpenAI؟', a: 'نعم — Codex CLI الرسمي من OpenAI بيشتغل على مفتاحك مباشرة. عندنا installer جاهز لـ Termux (أندرويد) و Linux/macOS بيسطّب Codex ويظبّط 23 profile مختلف (كل موديل ليه alias). الأمر واحد: curl -fsSL https://soal.help/install.sh | bash ثم soal gpt5 أو soal claude-sonnet-46.' },
  { q: 'هل يشتغل مع n8n و Cursor وأدوات الأتمتة؟', a: 'نعم — أي أداة بتدعم تغيير الـ Base URL بتشتغل فورًا. n8n و Make للأتمتة، Cursor و Continue و Cline و Aider للبرمجة، Chatbox و Cherry Studio و Open WebUI و AnythingLLM و TypingMind للدردشة، LangChain و LlamaIndex و ai-sdk للتطوير. الشرح خطوة بخطوة موجود في دليل الربط.' },
  { q: 'هل الأسعار مضاف عليها هامش كبير؟', a: 'لا. سعر البيع تقريبًا نفس السعر الرسمي الأمريكي محوّل للجنيه المصري بسعر الصرف الحالي — بدون هامش أرباح ضخم. مثال: GPT-5.4 = 240 ج.م لكل مليون توكن (نفس $5 الرسميين تقريبًا). الفرق إن هنا مش محتاج فيزا ولا حساب أجنبي، ومش هيتخصم منك اشتراك شهري لو مش شغّال.' },
  { q: 'سألت النموذج «إنت موديل كام؟» فقال إصدار أقدم — هل الموديل مضروب؟', a: 'لا إطلاقًا، ودي ظاهرة معروفة عالميًا بتحصل حتى مع الواجهات الرسمية. أي نموذج ذكاء اصطناعي بيتدرب على بيانات اتجمعت قبل إطلاقه هو نفسه، فمش بيعرف اسمه أو رقم إصداره وبيخمّن إصدارًا أقدم مشهورًا. طلبك بيتوجه للنموذج الحقيقي الذي اخترته بالظبط، والفوترة بتتم على نفس النموذج، والـ API بيرجع اسم النموذج الحقيقي في حقل model في كل رد. عندنا صفحة «تحقّق من أصالة الموديلات» فيها Function Calling Live Test بـ tool_call ID الأصلي من المزوّد.' },
  { q: 'إيه أرخص موديل عندكم للاستخدام اليومي؟', a: 'GPT-5 Nano (6 ج.م / 50 ج.م لكل مليون توكن input/output) هو الأرخص، بعده Gemini 2.5 Flash و GPT-4.1 Mini. للمقارنة: سؤال بسيط بيكلّف تقريبًا 0.001 ج.م — يعني بـ 10 ج.م رصيد ترحيبي تقدر تسأل آلاف الأسئلة قبل ما ترجع تشحن.' },
  { q: 'إزاي أشحن رصيدي وإيه وسائل الدفع المتاحة؟', a: 'من صفحة «المحفظة والشحن»: فودافون كاش · اتصالات كاش · انستاباي · تحويل بنكي · محفظة رقمية. اختر المبلغ ووسيلة الدفع، حوّل المبلغ للرقم الظاهر، ثم أدخل الرقم الذي حوّلت منه وأرسل الطلب. تتم مراجعة الطلب خلال دقائق وإضافة الرصيد بالجنيه المصري. أول شحنة بتاخد بونص +100% (إن أكتر من 200 ج.م).' },
  { q: 'هل بياناتي ومحادثاتي آمنة وخصوصية؟', a: 'نعم — soal.help مش بيخزّن محتوى محادثاتك على السيرفر (فقط عدد التوكن للفوترة). مفتاحك مشفّر ويتم إبطاله فورًا لو ضغطت «إلغاء المفتاح». الاتصال HTTPS بالكامل مع Cloudflare protection و DMARC/CAA/DNSSEC مفعّلة. البيانات الشخصية (رقم الموبايل + الرصيد) بس هي اللي بتتخزّن في قاعدة بياناتنا في مصر.' },
];

/* ---------- bento features ---------- */
const bento = [
  {
    icon: InfinityIcon, title: 'بدون حدود ولا ليمت',
    desc: 'مفيش قيود على عدد الطلبات أو الموديلات. استخدم براحتك طول ما رصيدك متاح — أنت المتحكم الوحيد.',
    span: 'md:col-span-2', accent: true,
  },
  { icon: Wallet, title: 'دفع بالجنيه المصري', desc: 'اشحن وادفع بالجنيه، وخصم دقيق حسب الاستهلاك الفعلي.' },
  { icon: KeyRound, title: 'مفتاح واحد لكل الموديلات', desc: 'GPT · Claude · Gemini + توليد الصور والفيديو (Sora 2) من مفتاح API واحد بدون اشتراكات منفصلة.' },
  { icon: Zap, title: 'متوافق مع OpenAI 100%', desc: 'غيّر الـ base_url فقط — يشتغل فورًا مع n8n و Cursor و Chatbox وكل مكتباتك وأدواتك الحالية.', span: 'md:col-span-2' },
  { icon: LineChart, title: 'شفافية وتحكم كامل', desc: 'سجلات لحظية للاستهلاك والتكلفة بالجنيه لكل طلب.' },
  { icon: Lock, title: 'أمان المفاتيح', desc: 'مفاتيحك مشفّرة، وتقدر تلغيها في أي وقت بضغطة واحدة.' },
];

export default function Landing() {
  const [models, setModels] = useState([]);
  const [settings, setSettings] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [pf, setPf] = useState('all');
  const [showPricing, setShowPricing] = useState(false);
  const [pricingPulse, setPricingPulse] = useState(false);
  const [showOfficialToast, setShowOfficialToast] = useState(false);
  const [showToolsToast, setShowToolsToast] = useState(false);
  const { user, enterGuest } = useAuth();
  const navigate = useNavigate();

  useSeo({
    title: 'شراء AI API بالجنيه المصري — GPT و Claude و Gemini بمفتاح واحد | بوابة النماذج الاصطناعية',
    description: 'اشترِ AI API بالجنيه المصري في دقائق: مفتاح واحد يفتح GPT-5.6 Sol و Claude Sonnet 5 و Gemini و Sora 2. ادفع فودافون كاش بدون فيزا — رصيد ترحيبي مجاني + بونص شحن +100%.',
    keywords: [
      'شراء api ai بالجنيه المصري', 'شراء API ذكاء اصطناعي', 'AI API Gateway', 'بوابة API ذكاء اصطناعي',
      'شراء ChatGPT API', 'شراء مفتاح OpenAI بالجنيه', 'ChatGPT بالجنيه المصري', 'بديل ChatGPT Plus',
      'GPT API مصر', 'GPT-5.6 Sol', 'GPT-5.6', 'GPT-5.5', 'GPT-5.4',
      'Claude API مصر', 'Claude Sonnet 5', 'Claude Code بالمصري', 'Codex CLI مصر',
      'Gemini API مصر', 'Sora 2 مصر', 'Nano Banana', 'توليد صور بالذكاء الاصطناعي', 'توليد فيديو بالذكاء الاصطناعي',
      'OpenAI API بالجنيه', 'مفتاح API واحد', 'ChatGPT مصر بدون فيزا', 'ChatGPT API فودافون كاش',
      'أرخص AI API مصر', 'بديل OpenRouter عربي', 'soal.help', 'بوابة النماذج الاصطناعية',
    ],
    path: '/',
    jsonLd: [
      {
        '@type': 'Organization',
        '@id': `${SITE}/#organization`,
        name: 'بوابة النماذج الاصطناعية',
        alternateName: ['soal.help', 'AI Model Gateway'],
        url: SITE,
        logo: `${SITE}/icon-512.png`,
        areaServed: { '@type': 'Country', name: 'Egypt' },
      },
      {
        '@type': 'WebSite',
        name: 'بوابة النماذج الاصطناعية',
        url: SITE,
        inLanguage: 'ar-EG',
      },
      {
        '@type': 'FAQPage',
        mainEntity: FAQ.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'الرئيسية', item: SITE },
        ],
      },
    ],
  });

  // Open the pricing details and scroll to them (used by the nav link too).
  const openPricing = () => {
    setShowPricing(true);
    requestAnimationFrame(() => {
      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Draw attention with a brief pulse after the scroll settles.
      setTimeout(() => {
        setPricingPulse(true);
        setTimeout(() => setPricingPulse(false), 1800);
      }, 550);
    });
  };

  useEffect(() => {
    api.models().then((r) => setModels(r.data.models || [])).catch(() => {});
    api.publicSettings().then((r) => setSettings(r.data)).catch(() => {});
    api.contactMethods().then((r) => setContacts(r.data.methods || [])).catch(() => {});
  }, []);

  // Show two announcement popups sequentially (once per user each, tracked
  // via localStorage): (1) hsys.io — store management & POS system, then
  // when the user closes it → (2) Official Tools support (Claude Code +
  // Codex CLI). Appear 4s after landing.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const officialDone = localStorage.getItem('hsys_promo_dismissed_v1') === '1';
    const toolsDone = localStorage.getItem('official_tools_dismissed_v1') === '1';
    if (officialDone && toolsDone) return undefined;
    const timer = setTimeout(() => {
      if (!officialDone) setShowOfficialToast(true);
      else if (!toolsDone) setShowToolsToast(true);
    }, 4000);
    return () => clearTimeout(timer);
  }, []);

  const dismissOfficialToast = () => {
    setShowOfficialToast(false);
    try { localStorage.setItem('hsys_promo_dismissed_v1', '1'); } catch { /* noop */ }
    // Chain to the tools toast if the user hasn't seen it yet.
    if (typeof window !== 'undefined' && localStorage.getItem('official_tools_dismissed_v1') !== '1') {
      setTimeout(() => setShowToolsToast(true), 350);
    }
  };

  const dismissToolsToast = () => {
    setShowToolsToast(false);
    try { localStorage.setItem('official_tools_dismissed_v1', '1'); } catch { /* noop */ }
  };

  const chatModels = models.filter((m) => m.category === 'chat');
  const imageModels = models.filter((m) => m.category === 'image');
  const videoModels = models.filter((m) => m.category === 'video');
  const shownChat = sortByProvider(chatModels.filter((m) => pf === 'all' || m.provider === pf));
  const bonus = settings?.welcome_bonus_egp || 0;
  const depBonusPct = settings?.deposit_bonus_enabled ? (settings?.deposit_bonus_percent || 0) : 0;

  // Official subscriptions CTA removed per request — no longer offering
  // ChatGPT Plus / Claude Pro / Gemini Advanced resale on the landing page.

  const codeSample = `from openai import OpenAI

client = OpenAI(
    api_key="sk-...",              # مفتاحك من بوابة النماذج الاصطناعية
    base_url="${GATEWAY_BASE}"
)

resp = client.chat.completions.create(
    model="claude-fable-5",
    messages=[{"role": "user", "content": "أهلاً!"}]
)
print(resp.choices[0].message.content)`;

  const goStart = () => navigate(user ? (user.role === 'admin' ? '/admisoal' : '/app') : '/register');
  const goSupport = () => navigate(user ? '/app/support' : '/register?next=/app/support');
  // Enter the user area — as a guest if not logged in (browse without signing up).
  const enterApp = (path = '/app') => {
    if (!user) enterGuest();
    navigate(user && user.role === 'admin' ? '/admisoal' : path);
  };

  return (
    <LazyMotion features={loadMotionFeatures}>
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* ================= NAV ================= */}
      <header className="sticky top-0 z-50 border-b border-border/60 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-9 w-9 shrink-0 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shadow-lg shadow-primary/25">
              <Sparkles className="h-5 w-5" />
            </div>
            <span className="font-semibold text-base sm:text-lg truncate">بوابة النماذج الاصطناعية</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
            <button onClick={openPricing} className="hidden md:inline text-sm text-muted-foreground hover:text-foreground px-3 py-2 rounded-lg transition-colors" data-testid="landing-nav-pricing">الأسعار</button>
            <button onClick={() => navigate(user ? '/app/verify' : '/register?next=/app/verify')} className="hidden md:inline text-sm text-muted-foreground hover:text-foreground px-3 py-2 rounded-lg transition-colors" data-testid="landing-nav-verify">اختبر النماذج</button>
            <button onClick={() => navigate('/blog')} className="hidden md:inline text-sm text-muted-foreground hover:text-foreground px-3 py-2 rounded-lg transition-colors" data-testid="landing-nav-blog">المدونة</button>
            <a href="#how" className="hidden md:inline text-sm text-muted-foreground hover:text-foreground px-3 py-2 rounded-lg transition-colors">كيف يعمل</a>
            {user ? (
              <Button onClick={goStart} data-testid="landing-go-dashboard" className="ms-1">لوحة التحكم</Button>
            ) : (
              <>
                <Button variant="ghost" size="sm" className="px-2.5 sm:px-3" onClick={() => navigate('/login')} data-testid="landing-login-button">دخول</Button>
                <Button size="sm" className="px-3 sm:px-4 gap-1.5 shadow-lg shadow-primary/25" onClick={() => navigate('/register')} data-testid="landing-register-button">ابدأ الآن</Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ================= HERO ================= */}
      <section className="relative">
        <div className="mesh-bg" aria-hidden>
          <div className="mesh-blob mesh-blob-1" />
          <div className="mesh-blob mesh-blob-2" />
          <div className="mesh-blob mesh-blob-3" />
        </div>
        <div className="absolute inset-0 grid-pattern opacity-70" aria-hidden />
        <div className="absolute inset-0 noise opacity-50" aria-hidden />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-20 lg:pt-24 lg:pb-28 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <m.div variants={stagger} initial="hidden" animate="show" className="min-w-0">
            <m.div variants={fadeUp} className="mb-5 flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="gap-1.5 py-1.5 px-3 glass border-primary/20">
                <span className="h-2 w-2 rounded-full bg-[hsl(var(--success))] live-dot" />
                متوافق بالكامل مع OpenAI API
              </Badge>
              <button
                type="button"
                onClick={openPricing}
                data-testid="landing-hero-pricing-chip"
                className="group inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs sm:text-sm font-medium text-primary hover:bg-primary/15 hover:border-primary/60 active:scale-[0.97] transition-all"
              >
                <Tags className="h-3.5 w-3.5" />
                عرض الأسعار
                <ChevronDown className="h-3.5 w-3.5 opacity-70 group-hover:opacity-100 group-hover:translate-y-0.5 transition-all" />
              </button>
            </m.div>
            <m.h1 variants={fadeUp} className="text-[1.95rem] sm:text-4xl lg:text-[3.4rem] font-bold leading-[1.3] lg:leading-[1.18]">
              كل نماذج الذكاء الاصطناعي
              <br />
              <span className="text-primary">بمفتاح واحد بالجنيه المصري</span> — بدون حدود
            </m.h1>
            <m.p variants={fadeUp} className="mt-6 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              وصول غير محدود إلى <span className="text-foreground font-medium">GPT · Claude · Gemini</span> مع
              <span className="text-foreground font-medium"> توليد الصور والفيديو (Sora 2)</span> من واجهة واحدة،
              رصيد واحد بالجنيه المصري، وخصم دقيق حسب الاستهلاك. استخدم براحتك — مفيش ليمت.
            </m.p>
            <m.div variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" data-testid="landing-hero-hsys-cta" className="gap-2 text-sm sm:text-base h-11 sm:h-12 px-5 sm:px-6 whitespace-nowrap shadow-xl shadow-primary/40 hover:scale-[1.03] transition-transform relative overflow-hidden">
                <a href={HSYS_URL} target="_blank" rel="noopener noreferrer">
                  <Store className="h-[18px] w-[18px] sm:h-5 sm:w-5 shrink-0 relative z-10" />
                  <span className="relative z-10">hsys.io — سيستم لكل محل</span>
                  <span aria-hidden className="absolute inset-0 -translate-x-full animate-[shine_2.5s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/25 to-transparent" />
                </a>
              </Button>
              <Button size="lg" variant="outline" onClick={() => enterApp('/guide')} data-testid="landing-hero-guide-cta" className="h-11 sm:h-12 px-5 sm:px-6 text-sm sm:text-base glass whitespace-nowrap">
                <Terminal className="h-4 w-4 me-2 shrink-0" /> دليل الربط
              </Button>
            </m.div>
            <m.p variants={fadeUp} className="mt-3 text-xs sm:text-sm text-muted-foreground max-w-lg" data-testid="landing-hero-hsys-note">
              🏪 <span className="font-medium text-foreground">hsys.io</span>: سيستم إدارة محلات وكاشير كامل — مبيعات، مخزون، فواتير وتقارير — لأي محل أو نشاط تجاري.
            </m.p>
            <m.div variants={fadeUp} className="mt-7 flex flex-nowrap items-center gap-x-3 sm:gap-x-5 text-xs sm:text-sm text-muted-foreground overflow-x-auto scrollbar-none">
              {['بدون حدود استخدام', 'دفع بالجنيه', 'بدون كريدت كارد'].map((t) => (
                <span key={t} className="flex items-center gap-1.5 whitespace-nowrap shrink-0"><Check className="h-4 w-4 text-[hsl(var(--success))] shrink-0" /> {t}</span>
              ))}
            </m.div>
            <m.div variants={fadeUp} className="mt-4 flex flex-nowrap items-center gap-1.5 sm:gap-2 overflow-x-auto scrollbar-none" data-testid="landing-made-in-egypt">
              <span className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-primary/20 bg-primary/5 px-2.5 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium whitespace-nowrap shrink-0">
                <span aria-hidden>🇪🇬</span> صناعة مصرية 100%
              </span>
              <button
                type="button"
                onClick={() => navigate(user ? '/app/verify' : '/register?next=/app/verify')}
                className="group inline-flex items-center gap-1 sm:gap-1.5 rounded-full bg-[hsl(var(--success))] text-white px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold whitespace-nowrap shrink-0 shadow-lg shadow-[hsl(var(--success))]/30 hover:shadow-[hsl(var(--success))]/50 hover:brightness-110 active:scale-[0.96] transition-all"
                data-testid="landing-hero-verify-btn"
              >
                <ShieldCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                تحقّق من أصالة النماذج
                <ArrowLeft className="h-3.5 w-3.5 opacity-70 group-hover:opacity-100 group-hover:-translate-x-0.5 transition-all hidden sm:inline-block" />
              </button>
            </m.div>
            {bonus > 0 && (
              <m.p variants={fadeUp} className="mt-4 text-sm inline-flex items-center gap-2 rounded-full bg-accent/60 px-3 py-1.5">
                <Star className="h-4 w-4 text-[hsl(var(--warning))]" /> رصيد ترحيبي {egp(bonus)} عند التسجيل
              </m.p>
            )}
            {depBonusPct > 0 && (
              <m.p variants={fadeUp} className="mt-3 sm:ms-2 text-sm inline-flex items-center gap-2 rounded-full bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] px-3 py-1.5" data-testid="landing-deposit-bonus">
                <Gift className="h-4 w-4" /> بونص شحن +{Math.round(depBonusPct)}% على رصيدك
              </m.p>
            )}
          </m.div>

          {/* code card */}
          <m.div
            initial={{ opacity: 0, y: 30, rotateX: 8 }}
            animate={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            className="relative min-w-0"
          >
            <div className="absolute -inset-4 bg-primary/10 rounded-3xl blur-2xl" aria-hidden />
            <Card className="relative shadow-2xl border-primary/10 overflow-hidden">
              <CardContent className="p-0">
                <div className="flex items-center gap-1.5 px-4 py-3 border-b bg-muted/40">
                  <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
                  <span className="h-3 w-3 rounded-full bg-[#febc2e]" />
                  <span className="h-3 w-3 rounded-full bg-[#28c840]" />
                  <span className="ms-3 text-xs text-muted-foreground font-mono">quickstart.py</span>
                </div>
                <CodeBlock code={codeSample} title="python" testid="landing-code" />
              </CardContent>
            </Card>
            <div className="mt-4 grid grid-cols-3 gap-3">
              {[{ i: Sparkles, t: 'Claude Sonnet 5' }, { i: Cpu, t: 'GPT-5.6 Sol' }, { i: Gauge, t: 'Gemini 3.1' }].map((c, idx) => (
                <m.div
                  key={idx}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + idx * 0.1 }}
                  className="glass rounded-xl px-3 py-3 flex items-center gap-2 text-sm border"
                >
                  <c.i className="h-4 w-4 text-primary" /> <span className="font-medium">{c.t}</span>
                </m.div>
              ))}
            </div>
          </m.div>
        </div>

        {/* marquee */}
        <div className="relative border-y bg-card/50 py-4 marquee-mask overflow-hidden">
          <div className="marquee-track">
            {[...models, ...models].map((m, i) => (
              <div key={i} className="flex items-center gap-2 rounded-full border bg-card px-4 py-2 text-sm whitespace-nowrap">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                <span className="font-medium">{m.display_name}</span>
                <span className="text-muted-foreground text-xs ltr">{m.model_id}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= STATS ================= */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { n: <Counter to={chatModels.length + imageModels.length + videoModels.length} suffix="+" />, l: 'موديل متاح' },
            { n: <><Counter to={3} /> </>, l: 'مزوّدين عالميين' },
            { n: <span className="inline-flex items-center gap-1"><InfinityIcon className="h-7 w-7" /></span>, l: 'حدود الاستخدام' },
            { n: <><Counter to={100} suffix="%" /></>, l: 'متوافق OpenAI' },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <Card className="text-center card-hover">
                <CardContent className="p-6">
                  <div className="text-3xl md:text-4xl font-bold text-primary">{s.n}</div>
                  <div className="text-sm text-muted-foreground mt-1">{s.l}</div>
                </CardContent>
              </Card>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ================= FEATURES (BENTO) ================= */}
      <section id="features" className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <Reveal className="text-center mb-12">
          <Badge variant="secondary" className="mb-3">لماذا بوابة النماذج الاصطناعية؟</Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">كل اللي محتاجه في مكان واحد</h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">مرونة كاملة، شفافية في التكلفة، وسهولة تكامل — بدون تعقيد.</p>
        </Reveal>

        <m.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-fr"
        >
          {bento.map((b, i) => (
            <m.div key={i} variants={fadeUp} className={b.span || ''}>
              <Card
                onMouseMove={onGlowMove}
                className={`h-full card-hover bento-glow ${b.accent ? 'bg-primary text-primary-foreground border-primary' : ''}`}
              >
                <CardContent className="p-6 h-full flex flex-col">
                  <div className={`h-11 w-11 rounded-xl flex items-center justify-center mb-4 ${b.accent ? 'bg-white/15' : 'bg-primary/10 text-primary'}`}>
                    <b.icon className="h-6 w-6" />
                  </div>
                  <p className="font-semibold text-lg">{b.title}</p>
                  <p className={`text-sm mt-1.5 leading-relaxed ${b.accent ? 'text-primary-foreground/85' : 'text-muted-foreground'}`}>{b.desc}</p>
                </CardContent>
              </Card>
            </m.div>
          ))}
        </m.div>
      </section>

      {/* ================= PRICING / MODELS ================= */}
      <section id="pricing" className={`relative scroll-mt-16 ${pricingPulse ? 'pricing-pulse' : ''}`}>
        <div className="bg-muted/40 border-y">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
            <Reveal className="text-center mb-10">
              <Badge variant="secondary" className="mb-3">تسعير شفّاف</Badge>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">الموديلات والأسعار</h2>
              <p className="text-muted-foreground mt-3">بالجنيه المصري — لكل مليون توكن (إدخال / إخراج). ادفع على اللي تستخدمه فقط.</p>
            </Reveal>

            {!showPricing ? (
              <Reveal className="text-center">
                <div className="relative mx-auto max-w-2xl rounded-2xl border bg-card p-8 sm:p-10 overflow-hidden" data-testid="landing-pricing-teaser">
                  <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden />
                  <div className="relative">
                    <div className="flex flex-wrap items-center justify-center gap-2 mb-5">
                      {[
                        { icon: Cpu, t: `${chatModels.length || '—'} موديل شات` },
                        { icon: ImageIcon, t: `${imageModels.length || '—'} موديل صور` },
                        { icon: Video, t: `${videoModels.length || '—'} موديل فيديو` },
                      ].map((c, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 rounded-full border bg-background px-3 py-1.5 text-sm font-medium">
                          <c.icon className="h-4 w-4 text-primary" /> {c.t}
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto leading-relaxed">
                      GPT · Claude · Gemini · Nano Banana · Sora 2 — كل الأسعار معلنة وشفافة بالجنيه المصري، بدون اشتراكات ولا رسوم مخفية.
                    </p>
                    <Button
                      size="lg"
                      onClick={() => setShowPricing(true)}
                      data-testid="landing-show-pricing-btn"
                      className="gap-2 h-12 px-6 sm:px-8 text-sm sm:text-base shadow-xl shadow-primary/30 hover:scale-[1.02] transition-transform"
                    >
                      <Tags className="h-5 w-5 shrink-0" /> اعرض كل الأسعار والتفاصيل <ChevronDown className="h-4 w-4 shrink-0" />
                    </Button>
                  </div>
                </div>
              </Reveal>
            ) : (
            <m.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              data-testid="landing-pricing-details"
            >
            <m.div
              variants={stagger}
              initial="hidden"
              animate="show"
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {chatModels.map((mdl) => {
                const discounted = (mdl.discount_percent || 0) > 0;
                return (
                <m.div key={mdl.id} variants={fadeUp}>
                  <Card className="card-hover bento-glow h-full" onMouseMove={onGlowMove} data-testid={`landing-model-${mdl.model_id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-2 gap-2">
                        <ProviderBadge provider={mdl.provider} />
                        <div className="flex items-center gap-1.5 flex-wrap justify-end">
                          {discounted && <DiscountBadge percent={mdl.discount_percent} testid={`landing-discount-${mdl.model_id}`} />}
                          {mdl.badge && <Badge variant="secondary">{mdl.badge}</Badge>}
                        </div>
                      </div>
                      <p className="font-semibold text-lg">{mdl.display_name}</p>
                      <p className="text-sm text-muted-foreground mt-1 min-h-[40px]">{mdl.description}</p>
                      <div className="mt-4 pt-4 border-t flex items-start justify-between gap-2 text-sm">
                        <div>
                          <p className="text-muted-foreground text-xs mb-0.5">إدخال / 1M</p>
                          <PriceTag price={mdl.sell_input_egp_per_1m} original={mdl.original_sell_input_egp_per_1m} discounted={discounted} />
                        </div>
                        <div className="text-start">
                          <p className="text-muted-foreground text-xs mb-0.5">إخراج / 1M</p>
                          <PriceTag price={mdl.sell_output_egp_per_1m} original={mdl.original_sell_output_egp_per_1m} discounted={discounted} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </m.div>
                );
              })}
            </m.div>

            {imageModels.length > 0 && (
              <m.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-6 grid md:grid-cols-3 gap-4">
                {imageModels.map((mdl) => (
                  <m.div key={mdl.id} variants={fadeUp}>
                    <Card className="card-hover h-full">
                      <CardContent className="p-5 flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1"><ImageIcon className="h-4 w-4 text-primary" /><span className="font-semibold">{mdl.display_name}</span></div>
                          <p className="text-sm text-muted-foreground">{mdl.description}</p>
                        </div>
                        <div className="text-center shrink-0 ms-3">
                          <p className="text-xs text-muted-foreground">لكل صورة</p>
                          <p className="font-semibold tabular-nums">{egp(mdl.sell_image_egp)}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </m.div>
                ))}
              </m.div>
            )}

            {videoModels.length > 0 && (
              <m.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-6 grid md:grid-cols-3 gap-4" data-testid="landing-video-models">
                {videoModels.map((mdl) => (
                  <m.div key={mdl.id} variants={fadeUp}>
                    <Card className="card-hover h-full" data-testid={`landing-video-${mdl.model_id}`}>
                      <CardContent className="p-5 flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1"><Video className="h-4 w-4 text-primary" /><span className="font-semibold">{mdl.display_name}</span></div>
                          <p className="text-sm text-muted-foreground">{mdl.description}</p>
                        </div>
                        <div className="text-center shrink-0 ms-3">
                          <p className="text-xs text-muted-foreground">لكل ثانية فيديو</p>
                          <p className="font-semibold tabular-nums">{egp(mdl.sell_video_egp_per_sec)}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </m.div>
                ))}
              </m.div>
            )}

            <div className="text-center mt-8">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowPricing(false);
                  requestAnimationFrame(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
                }}
                data-testid="landing-hide-pricing-btn"
                className="gap-1.5 text-muted-foreground hover:text-foreground"
              >
                <ChevronUp className="h-4 w-4" /> إخفاء الأسعار
              </Button>
            </div>
            </m.div>
            )}
          </div>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section id="how" className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <Reveal className="text-center mb-12">
          <Badge variant="secondary" className="mb-3">خطوات بسيطة</Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">كيف يعمل؟</h2>
        </Reveal>
        <m.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} className="grid md:grid-cols-3 gap-6">
          {[
            { n: '1', i: Wallet, t: 'سجّل واشحن رصيدك', d: 'أنشئ حسابًا برقم موبايلك واشحن رصيدك بالجنيه.' },
            { n: '2', i: KeyRound, t: 'أنشئ مفتاح API', d: 'مفتاح واحد يفتح لك كل الموديلات دفعة واحدة.' },
            { n: '3', i: Layers, t: 'ابدأ الاستخدام', d: 'ضع المفتاح في برنامجك واختر الموديل في كل طلب.' },
          ].map((s) => (
            <m.div key={s.n} variants={fadeUp}>
              <Card className="h-full card-hover relative overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="h-12 w-12 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold">{s.n}</div>
                    <s.i className="h-8 w-8 text-primary/30" />
                  </div>
                  <p className="font-semibold text-lg">{s.t}</p>
                  <p className="text-muted-foreground mt-1.5">{s.d}</p>
                </CardContent>
              </Card>
            </m.div>
          ))}
        </m.div>
      </section>

      {/* ================= TOOLS ================= */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14" data-testid="landing-tools">
        <Reveal className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">يشتغل مع أدواتك المفضلة فورًا</h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
            مفتاح واحد متوافق مع OpenAI — وصّله بأي أداة بتدعم تغيير الـ Base URL، من غير سطر كود إضافي.
          </p>
        </Reveal>
        <Reveal>
          <div className="flex flex-wrap items-center justify-center gap-2.5" data-testid="landing-tools-chips">
            {['n8n', 'Cursor', 'Chatbox', 'Cherry Studio', 'Continue / Cline (VS Code)', 'Open WebUI', 'AnythingLLM', 'TypingMind', 'Dify', 'Flowise', 'Make', 'LangChain', 'مكتبات OpenAI الرسمية'].map((t) => (
              <span key={t} className="rounded-full border bg-card px-4 py-2 text-sm font-medium hover:border-primary/40 hover:text-primary transition-colors">{t}</span>
            ))}
          </div>
          <div className="text-center mt-7">
            <Button variant="outline" onClick={() => navigate('/guide')} data-testid="landing-tools-guide-btn">
              شرح ربط كل أداة خطوة بخطوة <ArrowLeft className="h-4 w-4 ms-2" />
            </Button>
          </div>
        </Reveal>
      </section>

      {/* ================= TRUST / VERIFY ================= */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14" data-testid="landing-trust">
        <Reveal>
          <Card className="relative overflow-hidden border-primary/20">
            <div className="absolute inset-0 mesh-bg opacity-40" aria-hidden>
              <div className="mesh-blob mesh-blob-1" />
              <div className="mesh-blob mesh-blob-3" />
            </div>
            <CardContent className="relative p-8 md:p-12">
              <div className="grid md:grid-cols-3 gap-8 items-center">
                <div className="md:col-span-2">
                  <Badge variant="secondary" className="mb-3 gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--success))]" />
                    شفافية كاملة
                  </Badge>
                  <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                    مش هتصدق كلامنا؟ <span className="text-primary">اختبر بنفسك.</span>
                  </h2>
                  <p className="text-muted-foreground mt-3 leading-relaxed max-w-xl">
                    سجّل مجاناً، خد رصيدك الترحيبي، واختبر أي نموذج بنفسك — Live Chat +
                    <span className="font-mono ltr mx-1">Function Calling</span> حقيقي مع
                    الـ <span className="font-mono ltr">tool_call ID</span> الأصلي من المزوّد.
                    كل اختبار بيتخصم من رصيدك بالضبط زي أي استخدام عادي.
                  </p>
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    <Button size="lg" onClick={() => navigate(user ? '/app/verify' : '/register?next=/app/verify')} data-testid="landing-verify-cta" className="gap-2 h-11 shadow-lg shadow-primary/25">
                      <Wrench className="h-4 w-4" /> {user ? 'افتح صفحة الاختبار' : 'سجّل واختبر بنفسك'}
                    </Button>
                    <div className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
                      <Award className="h-4 w-4 text-[hsl(var(--success))]" />
                      <span>تحدّي أصالة النماذج: <span className="font-semibold text-foreground">رصيدك × 2</span></span>
                    </div>
                  </div>
                </div>
                <div className="hidden md:flex justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full" />
                    <div className="relative h-32 w-32 rounded-3xl bg-gradient-to-br from-primary to-primary/60 text-primary-foreground flex items-center justify-center shadow-2xl">
                      <ShieldCheck className="h-16 w-16" strokeWidth={1.5} />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </Reveal>
      </section>

      {/* ================= OFFICIAL SUBSCRIPTIONS (removed) ================= */}

      {/* ================= FAQ ================= */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <Reveal className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">أسئلة شائعة</h2>
        </Reveal>
        <m.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }} className="space-y-3">
          {FAQ.map((f, i) => (
            <m.div key={i} variants={fadeUp}>
              <Card className="card-hover"><CardContent className="p-5">
                <p className="font-semibold flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-primary" /> {f.q}</p>
                <p className="text-sm text-muted-foreground mt-2 ps-3.5 leading-relaxed">{f.a}</p>
              </CardContent></Card>
            </m.div>
          ))}
        </m.div>
      </section>

      {/* ================= CTA ================= */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
        <Reveal>
          <Card className="relative overflow-hidden border-primary/20">
            <div className="absolute inset-0 bg-primary" />
            <div className="mesh-bg opacity-60" aria-hidden><div className="mesh-blob mesh-blob-2" /><div className="mesh-blob mesh-blob-3" /></div>
            <div className="absolute inset-0 noise opacity-25" aria-hidden />
            <CardContent className="relative p-10 md:p-14 text-center text-primary-foreground">
              <h3 className="text-3xl md:text-4xl font-bold">جاهز تبدأ؟</h3>
              <p className="mt-3 opacity-90 text-lg">أنشئ حسابك الآن واحصل على مفتاحك خلال دقائق — بدون حدود.</p>
              <Button size="lg" variant="secondary" className="mt-7 h-12 px-8 text-base hover:scale-[1.02] transition-transform" onClick={goStart} data-testid="landing-cta-bottom">
                ابدأ الآن مجانًا <ArrowLeft className="h-4 w-4 ms-2" />
              </Button>
            </CardContent>
          </Card>
        </Reveal>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="border-t">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
          {contacts.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3" data-testid="landing-contacts">
              <span className="text-sm text-muted-foreground">تواصل معنا:</span>
              <div className="flex flex-wrap items-center justify-center gap-2">
                {contacts.map((m) => {
                  const Icon = contactIconFor(m.type);
                  const isDirect = m.type === 'phone' || m.type === 'email';
                  return (
                    <a
                      key={m.id}
                      href={contactHref(m.type, m.value)}
                      target={isDirect ? undefined : '_blank'}
                      rel="noopener noreferrer"
                      data-testid={`landing-contact-${m.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm hover:border-primary/40 hover:text-primary transition-colors"
                    >
                      <Icon className="h-4 w-4" /> {m.label}
                    </a>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
              <span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> بوابة النماذج الاصطناعية © {new Date().getFullYear()}</span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-2.5 py-1 text-xs font-medium" data-testid="footer-made-in-egypt"><span aria-hidden>🇪🇬</span> صناعة مصرية 100%</span>
            </div>
            <div className="flex items-center gap-4 flex-wrap justify-center">
              <button type="button" onClick={goSupport} className="hover:text-foreground transition-colors" data-testid="footer-support">تواصل مع الدعم</button>
              <button type="button" onClick={() => navigate(user ? '/app/verify' : '/register?next=/app/verify')} className="hover:text-foreground transition-colors" data-testid="footer-verify">اختبر النماذج</button>
              <Link to="/features" className="hover:text-foreground transition-colors" data-testid="footer-features">المميزات</Link>
              <Link to="/guide" className="hover:text-foreground transition-colors" data-testid="footer-guide">دليل الربط</Link>
              <Link to="/blog" className="hover:text-foreground transition-colors" data-testid="footer-blog">المدونة</Link>
              <Link to="/login" className="hover:text-foreground transition-colors">دخول</Link>
              <Link to="/register" className="hover:text-foreground transition-colors">تسجيل</Link>
            </div>
          </div>
          <div className="pt-4 border-t flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-muted-foreground" data-testid="footer-seo-links">
            <Link to="/buy-ai-api-egypt" className="hover:text-primary transition-colors">شراء AI API بالجنيه</Link>
            <span aria-hidden>·</span>
            <Link to="/ai-api-gateway" className="hover:text-primary transition-colors">AI API Gateway</Link>
            <span aria-hidden>·</span>
            <Link to="/gpt-api-egypt" className="hover:text-primary transition-colors">GPT API مصر</Link>
            <span aria-hidden>·</span>
            <Link to="/claude-api-egypt" className="hover:text-primary transition-colors">Claude API مصر</Link>
            <span aria-hidden>·</span>
            <Link to="/gemini-api-egypt" className="hover:text-primary transition-colors">Gemini API مصر</Link>
            <span aria-hidden>·</span>
            <Link to="/ai-api-without-visa" className="hover:text-primary transition-colors">API بدون فيزا</Link>
            <span aria-hidden>·</span>
            <Link to="/compare/chatgpt-plus" className="hover:text-primary transition-colors">بديل ChatGPT Plus</Link>
            <span aria-hidden>·</span>
            <a href="https://autovideo.best" target="_blank" rel="noopener noreferrer" data-testid="footer-autovideo" className="hover:text-primary transition-colors">من مشاريعنا: AutoVideo</a>
          </div>
          <div className="pt-4 border-t flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-muted-foreground" data-testid="footer-legal">
            <Link to="/terms" className="hover:text-primary transition-colors" data-testid="footer-terms">الشروط والأحكام</Link>
            <span aria-hidden>·</span>
            <Link to="/privacy" className="hover:text-primary transition-colors" data-testid="footer-privacy">سياسة الخصوصية</Link>
            <span aria-hidden>·</span>
            <Link to="/aup" className="hover:text-primary transition-colors" data-testid="footer-aup">الاستخدام المقبول</Link>
          </div>
        </div>
      </footer>

      {/* ============ Dismissible announcements (chained) ============ */}
      {/* Toast 1: hsys.io — store management & POS system */}
      <AnimatePresence>
        {showOfficialToast && (
          <m.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-4 start-4 z-[60] w-[min(360px,calc(100vw-2rem))] pointer-events-auto"
            data-testid="landing-hsys-toast"
          >
            <div className="relative rounded-2xl border border-[hsl(var(--success))]/30 bg-card/95 backdrop-blur-xl shadow-2xl shadow-[hsl(var(--success))]/20 p-4 pt-11 sm:pt-4 sm:pe-14 overflow-hidden">
              <div className="absolute -top-16 -right-16 h-32 w-32 rounded-full bg-[hsl(var(--success))]/25 blur-3xl pointer-events-none" aria-hidden />
              <button
                type="button"
                onClick={dismissOfficialToast}
                aria-label="إغلاق"
                data-testid="landing-hsys-toast-close"
                className="absolute top-1.5 end-1.5 h-10 w-10 rounded-full inline-flex items-center justify-center bg-muted/60 hover:bg-muted text-foreground/70 hover:text-foreground active:scale-95 transition-all touch-manipulation"
              >
                <X className="h-5 w-5" strokeWidth={2.5} />
              </button>
              <div className="relative flex items-start gap-3">
                <div className="h-10 w-10 shrink-0 rounded-xl bg-gradient-to-br from-[hsl(var(--success))] to-[hsl(var(--success))]/70 text-white flex items-center justify-center shadow-lg shadow-[hsl(var(--success))]/30">
                  <Store className="h-5 w-5" strokeWidth={2.2} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="inline-flex items-center rounded-full bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] text-[10px] font-semibold px-1.5 py-0.5 tracking-wide">جديد</span>
                    <span className="text-[11px] text-muted-foreground">سيستم لكل محل</span>
                  </div>
                  <div className="text-sm font-semibold leading-snug">
                    <span className="text-[hsl(var(--success))] ltr inline-block">hsys.io</span> — برنامج إدارة محلات وكاشير
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    مبيعات، مخزون، فواتير وتقارير — سيستم كامل لأي محل أو نشاط تجاري. جرّبه دلوقتي.
                  </p>
                  <div className="mt-2.5 flex items-center gap-2">
                    <a
                      href={HSYS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={dismissOfficialToast}
                      data-testid="landing-hsys-toast-cta"
                      className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--success))] hover:brightness-110 text-white px-3 py-1.5 text-xs font-semibold shadow-lg shadow-[hsl(var(--success))]/30 transition-all"
                    >
                      <Store className="h-3.5 w-3.5" /> افتح hsys.io
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </m.div>
        )}
      </AnimatePresence>

      {/* Toast 2: Official Tools (Claude Code + Codex CLI) — appears after
          the first toast is dismissed (or immediately on next visit). */}
      <AnimatePresence>
        {showToolsToast && (
          <m.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-4 start-4 z-[60] w-[min(360px,calc(100vw-2rem))] pointer-events-auto"
            data-testid="landing-tools-toast"
          >
            <div className="relative rounded-2xl border border-primary/25 bg-card/95 backdrop-blur-xl shadow-2xl shadow-primary/20 p-4 pt-11 sm:pt-4 sm:pe-14 overflow-hidden">
              <div className="absolute -top-16 -right-16 h-32 w-32 rounded-full bg-primary/25 blur-3xl pointer-events-none" aria-hidden />
              <button
                type="button"
                onClick={dismissToolsToast}
                aria-label="إغلاق"
                data-testid="landing-tools-toast-close"
                className="absolute top-1.5 end-1.5 h-10 w-10 rounded-full inline-flex items-center justify-center bg-muted/60 hover:bg-muted text-foreground/70 hover:text-foreground active:scale-95 transition-all touch-manipulation"
              >
                <X className="h-5 w-5" strokeWidth={2.5} />
              </button>
              <div className="relative flex items-start gap-3">
                <div className="h-10 w-10 shrink-0 rounded-xl bg-gradient-to-br from-primary to-primary/70 text-primary-foreground flex items-center justify-center shadow-lg shadow-primary/30">
                  <BadgeCheck className="h-5 w-5" strokeWidth={2.2} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="inline-flex items-center rounded-full bg-primary/15 text-primary text-[10px] font-semibold px-1.5 py-0.5 tracking-wide">جديد</span>
                    <span className="text-[11px] text-muted-foreground">دعم رسمي</span>
                  </div>
                  <div className="text-sm font-semibold leading-snug">
                    شغّل <span className="text-primary">Claude Code</span> و <span className="text-primary">Codex CLI</span> بمفتاحك
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    الأدوات الرسمية من Anthropic و OpenAI شغّالة على soal بالجنيه — من غير VPN ولا Visa.
                  </p>
                  <div className="mt-2.5 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => { dismissToolsToast(); navigate('/guide'); }}
                      data-testid="landing-tools-toast-cta"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                    >
                      شوف الطريقة <ArrowLeft className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
    </LazyMotion>
  );
}
